import React, { useState, useEffect, useCallback } from 'react';
import ReactLoader from '@shared/components/core/ReactLoader';
import HomeStore from './stores/HomeStore';
import FacilityList from './components/FacilityList';
import KnowledgeBooks from './components/KnowledgeBooks';
import { Ajax, L10n, loadCldr, setCulture } from '@syncfusion/ej2-base';

declare var InovxLoader: any;

InovxLoader.addLanguage('home');

let isJS2LocaleLoaded = false;

const getHomeTranslation = (key: string) => {
    const translationKey = `home:${key}`;
    const translatedValue = InovxLoader.t(translationKey);
    return translatedValue !== translationKey ? translatedValue : '';
};

const loadHomeJS2LocaleOverrides = (syncfusionLocale: string) => {
    if (syncfusionLocale !== 'ja') {
        return;
    }

    const carouselLocale = {
        nextSlide: getHomeTranslation('carousel.nextSlide'),
        of: getHomeTranslation('carousel.of'),
        pauseSlideTransition: getHomeTranslation('carousel.pauseSlideTransition'),
        playSlideTransition: getHomeTranslation('carousel.playSlideTransition'),
        previousSlide: getHomeTranslation('carousel.previousSlide'),
        slide: getHomeTranslation('carousel.slide'),
        slideShow: getHomeTranslation('carousel.slideShow')
    };

    if (Object.values(carouselLocale).some((value) => !value)) {
        return;
    }

    L10n.load({
        ja: {
            carousel: carouselLocale
        }
    });
};

const loadJsonFromUrl = (jsonUrl: string, callback: (data: JSON) => void, onFailure?: () => void) => {
    const ajax = new Ajax(jsonUrl, 'GET', true);
    ajax.onSuccess = (value: any) => {
        callback(JSON.parse(value));
    };
    ajax.onFailure = () => {
        if (onFailure) {
            onFailure();
        }
    };
    ajax.send();
};

const loadCldrJsonFromUrl = (cldrJsonUrl: string) => {
    loadJsonFromUrl(cldrJsonUrl, (data: JSON) => {
        loadCldr(data);
    });
};

const loadJS2LocaleData = (callback: () => void) => {
    const syncfusionLocale = InovxLoader.getSyncfusionJS2Locale ? InovxLoader.getSyncfusionJS2Locale() : '';
    (window as any).SYNCFUSION_LOCALE = syncfusionLocale || null;

    if (!syncfusionLocale) {
        callback();
        return;
    }

    setCulture(syncfusionLocale);
    loadHomeJS2LocaleOverrides(syncfusionLocale);

    if (isJS2LocaleLoaded) {
        callback();
        return;
    }

    const cldrJsonUrls = InovxLoader.getCldrJsonUrls ? InovxLoader.getCldrJsonUrls() as string[] : null;
    if (cldrJsonUrls) {
        cldrJsonUrls.forEach((value) => {
            loadCldrJsonFromUrl(value);
        });
    }

    const jsonUrl = InovxLoader.getSyncfusionJS2JsonUrl ? InovxLoader.getSyncfusionJS2JsonUrl() : null;
    if (!jsonUrl) {
        isJS2LocaleLoaded = true;
        callback();
        return;
    }

    loadJsonFromUrl(jsonUrl, (data: JSON) => {
        L10n.load(data);
        isJS2LocaleLoaded = true;
        callback();
    }, () => {
        callback();
    });
};

/**
 * Home page content — rendered inside AdminLayout.
 * Shows the facility carousel and recent knowledge books
 * when the user has the appropriate permissions.
 */
const HomeApp: React.FC = () => {
    const [isLocaleReady, setIsLocaleReady] = useState(false);
    const [errorStatus, setErrorStatus] = useState<number>();
    const [hasFacilityPermission, setHasFacilityPermission] = useState(
        HomeStore.hasFacilityExplorePermission()
    );

    const onStoreChange = useCallback(() => {
        setHasFacilityPermission(HomeStore.hasFacilityExplorePermission());
    }, []);

    const handleLoadError = useCallback((error: any) => {
        setErrorStatus(typeof error?.status === 'number' ? error.status : undefined);
    }, []);

    useEffect(() => {
        HomeStore.addChangeListener(onStoreChange);
        return () => HomeStore.removeChangeListener(onStoreChange);
    }, [onStoreChange]);

    useEffect(() => {
        let isCancelled = false;

        try {
            InovxLoader.updateHelp('HomePage');
        } catch (e) {
        }

        InovxLoader.loadedOnce(() => {
            loadJS2LocaleData(() => {
                if (!isCancelled) {
                    setIsLocaleReady(true);
                }
            });
        });

        return () => {
            isCancelled = true;
        };
    }, []);

    return (
        <ReactLoader appReady={isLocaleReady} errorStatus={errorStatus}>
            {hasFacilityPermission ? (
                <>
                    <FacilityList onError={handleLoadError} />
                    <KnowledgeBooks onError={handleLoadError} />
                </>
            ) : null}
        </ReactLoader>
    );
};

export default HomeApp;
